import { useEffect, useState } from "react";
import { getUSPhoneDigits, formatUSPhone } from "../utils/phoneFormatter";
import axios from "axios";
import { ticketBaseURL, appId } from "../config/api";
import { Helmet } from "react-helmet-async";

export const ContactUs = () => {
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [description, setDescription] = useState("");

    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);

    const [isEmailValid, setIsEmailValid] = useState(true);
    const [isPhoneValid, setIsPhoneValid] = useState(true);

    const handlePhoneChangeLocal = (value: string) => {
        const input = document.activeElement as HTMLInputElement;

        if (!input || input.tagName !== "INPUT") {
            setPhone(getUSPhoneDigits(value));
            return;
        }

        const cursor = input.selectionStart || 0;

        const digitsBeforeCursor = value
            .slice(0, cursor)
            .replace(/\D/g, "").length;

        const digits = getUSPhoneDigits(value);

        setPhone(digits);

        requestAnimationFrame(() => {
            const formatted = formatUSPhone(digits);

            let newCursor = formatted.length;
            let digitCount = 0;

            for (let i = 0; i < formatted.length; i++) {
                if (/\d/.test(formatted[i])) digitCount++;

                if (digitCount >= digitsBeforeCursor) {
                    newCursor = i + 1;
                    break;
                }
            }

            try {
                input.setSelectionRange(newCursor, newCursor);
            } catch {
                //
            }
        });
    };

    useEffect(() => {
        let timer: NodeJS.Timeout;

        if (email.trim() !== "") {
            timer = setTimeout(() => {
                const valid =
                    /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/.test(email.trim());

                setIsEmailValid(valid);
            }, 800);
        } else {
            setIsEmailValid(true);
        }

        return () => clearTimeout(timer);
    }, [email]);

    useEffect(() => {
        let timer: NodeJS.Timeout;

        if (phone.trim() !== "") {
            timer = setTimeout(() => {
                const valid = /^\d{10}$/.test(phone);
                setIsPhoneValid(valid);
            }, 500);
        } else {
            setIsPhoneValid(true);
        }

        return () => clearTimeout(timer);
    }, [phone]);

    const isFormValid =
        name.trim() !== "" &&
        email.trim() !== "" &&
        phone.trim() !== "" &&
        description.trim() !== "" &&
        isEmailValid &&
        isPhoneValid &&
        !loading;

    const resetForm = () => {
        setName("");
        setPhone("");
        setEmail("");
        setDescription("");
    };

    const handleSubmit = async (
        e: React.FormEvent<HTMLFormElement>
    ) => {
        e.preventDefault();

        if (!isFormValid) return;

        setLoading(true);

        try {
            await axios.post(
                `${ticketBaseURL}customerEnquiry`,
                JSON.stringify({
                    email,
                    name,
                    phone,
                    question: description,
                    applicationId: appId,
                    created: new Date(),
                }),
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    withCredentials: true,
                }
            );

            resetForm();
            setSubmitted(true);
            window.scrollTo({
                top: 250,
                behavior: "smooth",
            });

            setTimeout(() => {
                setSubmitted(false);
            }, 5000);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <section className="bg-primary">
            <Helmet>
                <title>Contact Velox Charter | Request a Transportation Quote</title>

                <meta
                    name="description"
                    content="Contact Velox Charter for professional charter transportation services. Request a free quote for airport transfers, corporate transportation, group travel, employee shuttles, event transportation, and private charter services."
                />
            </Helmet>

            <div className="max-w-7xl mx-auto px-4">
                <div className="h-[220px] md:h-[280px] flex items-center justify-center">
                    <h1 className="text-white text-4xl md:text-6xl font-bold text-center">
                        Contact Us
                    </h1>
                </div>
            </div>

            <section className="bg-[#f5f5f5] py-16 lg:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-[1fr_320px] gap-10 lg:gap-8">

                        <div>
                            <form onSubmit={handleSubmit}>
                                {submitted && (
                                    <div className="mb-4 p-4 rounded-md bg-green-100 text-green-700">
                                        Thank you! We'll be in touch soon.
                                    </div>
                                )}

                                <div className="grid md:grid-cols-2 gap-5">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Full Name
                                        </label>

                                        <input
                                            type="text"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            placeholder="Enter your full name"
                                            className=" w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Email Address
                                        </label>

                                        <input
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="Email Address"
                                            className=" w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                            required
                                        />

                                        {!isEmailValid && (
                                            <p className="text-xs text-red-500 mt-1">
                                                Please enter a valid email address
                                            </p>
                                        )}
                                    </div>

                                </div>

                                <div className="mt-5">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Phone Number
                                    </label>

                                    <input
                                        type="text"
                                        value={formatUSPhone(phone)}
                                        onChange={(e) =>
                                            handlePhoneChangeLocal(e.target.value)
                                        }
                                        placeholder="(281) 999-9005"
                                        className=" w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                        required
                                    />

                                    {!isPhoneValid && (
                                        <p className="text-xs text-red-500 mt-1">
                                            Please enter a valid 10-digit phone number
                                        </p>
                                    )}
                                </div>

                                <div className="mt-5">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Description
                                    </label>

                                    <textarea
                                        rows={7}
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        placeholder="How can we help?"
                                        className="w-full p-4 border border-gray-300 rounded-md bg-white resize-none outline-none focus:border-primary"
                                        required
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={!isFormValid}
                                    className=" w-full mt-5 h-12 bg-primary text-white font-medium rounded-md hover:bg-[#004b86] transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? "Sending..." : "Send"}
                                </button>
                            </form>
                        </div>

                        <div className="space-y-8">

                            <div className="flex items-start gap-4">
                                <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center shrink-0">
                                    <span className="material-symbols-outlined text-[24px] text-white">
                                        call
                                    </span>
                                </div>

                                <div>
                                    <h3 className="font-semibold text-black">
                                        Call Us
                                    </h3>

                                    <p className="mt-2 text-gray-500">
                                        (281) 720-3007
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start gap-4">
                                <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center shrink-0">
                                    <span className="material-symbols-outlined text-[24px] text-white">
                                        mail
                                    </span>
                                </div>

                                <div>
                                    <h3 className="font-semibold text-black">
                                        Email Us
                                    </h3>

                                    <p className="mt-2 text-gray-500 break-all">
                                        info@veloxcharter.com
                                    </p>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </section>
        </section>
    );
};

export default ContactUs;